import { supabase } from '@/integrations/supabase/client';
import { UIMessage } from '@/hooks/useMessages';

interface DbMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  is_read: boolean;
  is_booking_request: boolean;
  content_type: string;
  attachment_url?: string | null;
  attachment_type?: string | null;
  sender_type?: string | null;
  user_id?: string;
}

export const getProfileName = async (userId: string): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw error;
    return data?.display_name || 'User';
  } catch (error) {
    console.error(`Error fetching profile name for ${userId}:`, error);
    return 'User';
  }
};

export const formatMessage = (msg: DbMessage, profilesCache: Map<string, string>): UIMessage => {
  return {
    id: msg.id,
    senderId: msg.sender_id,
    recipientId: msg.recipient_id,
    content: msg.content,
    timestamp: new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    isSent: true,
    isDelivered: true,
    isRead: msg.is_read,
    isBookingRequest: msg.is_booking_request,
    contentType: msg.content_type || 'text',
    attachmentUrl: msg.attachment_url,
    attachmentType: msg.attachment_type,
    senderName: profilesCache.get(msg.sender_id) || 'User',
    senderType: msg.sender_type as ('user' | 'escort' | 'ai') || 'user',
  };
};

export const fetchAndFormatMessages = async (userId: string, partnerId: string, conversationId: string): Promise<UIMessage[]> => {
  console.log(`Fetching messages for conversation: ${conversationId}`);
  const { data: messagesData, error: fetchError } = await supabase
    .from('messages')
    .select<string, DbMessage>('*, sender_type')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (fetchError) {
    console.error('Error fetching messages:', fetchError);
    throw fetchError;
  }

  if (!messagesData) return [];

  const profileIdsToFetch = new Set<string>([userId, partnerId, ...messagesData.map(m => m.sender_id)]);
  const profilesCache = new Map<string, string>();
  const nameFetchPromises = Array.from(profileIdsToFetch).map(async (id) => {
    const name = await getProfileName(id);
    profilesCache.set(id, name);
  });
  await Promise.all(nameFetchPromises);

  const formattedMessages = messagesData.map(msg =>
    formatMessage(msg, profilesCache)
  );
  
  console.log(`Fetched and formatted ${formattedMessages.length} messages.`);
  return formattedMessages;
};

// Helper function to check if current time is within the AI schedule
const isWithinSchedule = (schedule: any): boolean => {
    // Schedule format example: {"monday": ["09:00-17:00"], "tuesday": ["09:00-12:00", "14:00-17:00"]}
    if (!schedule || typeof schedule !== 'object') return false; // No schedule or invalid format
    try {
        const now = new Date();
        // Use French locale for day name consistency if needed, otherwise default locale
        const dayOfWeek = now.toLocaleString('en-US', { weekday: 'long' }).toLowerCase(); // e.g., 'monday'
        const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

        const daySchedule = schedule[dayOfWeek];
        if (!daySchedule || !Array.isArray(daySchedule)) return false; // No schedule for today

        for (const timeRange of daySchedule) {
            if (typeof timeRange === 'string') {
                const [start, end] = timeRange.split('-');
                if (start && end && currentTime >= start && currentTime < end) {
                    console.log(`Current time ${currentTime} is within scheduled range ${timeRange} for ${dayOfWeek}.`);
                    return true;
                }
            }
        }
        console.log(`Current time ${currentTime} is outside scheduled ranges for ${dayOfWeek}.`);
        return false;
    } catch (error) {
        console.error("Error parsing AI schedule:", error);
        return false; // Fail safely, don't respond if schedule parsing fails
    }
};

export const sendAndFormatMessage = async (
  content: string,
  senderId: string,
  recipientId: string,
  conversationId: string,
  isBookingRequest: boolean = false,
  contentType: string = 'text',
  attachmentUrl: string | null = null,
  attachmentType: string | null = null
): Promise<UIMessage> => {
  const { data: newMessageData, error: insertError } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversationId,
      content,
      sender_id: senderId,
      recipient_id: recipientId,
      is_read: false,
      is_booking_request: isBookingRequest,
      content_type: contentType,
      attachment_url: attachmentUrl,
      attachment_type: attachmentType,
      sender_type: 'user'
    })
    .select<string, DbMessage>('*, sender_type')
    .single();
    
  if (insertError) {
    console.error("Error sending message:", insertError);
    throw insertError;
  }
  if (!newMessageData) {
    throw new Error("Failed to send message, no data returned.");
  }

  // --- START AI Trigger Logic ---
  try {
    // 1. Fetch recipient profile details including AI settings and role
    const { data: recipientProfile, error: profileError } = await supabase
      .from('profiles') 
      .select('role, ai_chat_enabled, ai_chat_response_mode, ai_chat_schedule')
      .eq('id', recipientId) 
      .single();

    if (profileError) {
      console.error(`Error fetching recipient profile for AI check (${recipientId}):`, profileError);
    } else if (recipientProfile && recipientProfile.role === 'escort') {
        console.log(`Recipient ${recipientId} is an escort. Checking AI settings...`, recipientProfile);
        const { ai_chat_enabled, ai_chat_response_mode, ai_chat_schedule } = recipientProfile;

        let shouldAiRespond = false;
        if (ai_chat_enabled) {
            if (ai_chat_response_mode === 'always') {
                shouldAiRespond = true;
                console.log(`AI for ${recipientId} is set to 'always'.`);
            } else if (ai_chat_response_mode === 'scheduled') {
                console.log(`AI for ${recipientId} is set to 'scheduled'. Checking schedule...`);
                if (isWithinSchedule(ai_chat_schedule)) {
                    shouldAiRespond = true;
                } else {
                     console.log(`AI for ${recipientId} is scheduled but current time is outside defined slots.`);
                }
            }
        } else {
             console.log(`AI chat is disabled for escort ${recipientId}.`);
        }

        if (shouldAiRespond) {
            console.log(`AI should respond for escort ${recipientId} in conversation ${conversationId}. Triggering Edge Function...`);
            // Invoke the Edge Function asynchronously (fire and forget)
            supabase.functions.invoke('groq-chat-handler', {
                body: {
                    conversation_id: conversationId,
                    user_message_content: content, // Le message de l'utilisateur
                    escort_user_id: recipientId,    // L'ID de l'escort (qui "répond" via l'IA)
                    human_user_id: senderId       // <-- AJOUTER : L'ID de l'utilisateur humain (destinataire de la réponse IA)
                }
            }).then(({ data, error: invokeError }) => {
                if (invokeError) {
                    console.error(`Error invoking groq-chat-handler for conversation ${conversationId}:`, invokeError);
                } else {
                    console.log(`groq-chat-handler invoked successfully for conversation ${conversationId}. Function response:`, data);
                }
            });
        }
    } else if (recipientProfile) {
        console.log(`Recipient ${recipientId} is not an escort (role: ${recipientProfile.role}). No AI trigger.`);
    } else {
        console.log(`Recipient profile ${recipientId} not found. No AI trigger.`);
    }
  } catch (aiCheckError) {
    console.error(`Critical error during AI check/trigger for recipient ${recipientId}:`, aiCheckError);
    // Log critical errors but do not block message sending
  }
  // --- END AI Trigger Logic ---

  // Format and return the user's message immediately
  const senderName = await getProfileName(senderId);
  const profilesCache = new Map([[senderId, senderName]]);
  const formattedMessage = formatMessage(newMessageData, profilesCache);
  
  console.log(`Message ${formattedMessage.id} sent successfully.`);
  return formattedMessage;
};

export const markSpecificMessagesAsRead = async (messageIds: string[]): Promise<void> => {
  if (!messageIds || messageIds.length === 0) return;
  
  console.log(`Marking ${messageIds.length} specific messages as read.`);
  const { error } = await supabase
    .from('messages')
    .update({ is_read: true })
    .in('id', messageIds);
    
  if (error) {
    console.error('Error marking specific messages as read:', error);
    throw error;
  }
};

export const subscribeToFormattedMessages = (
  conversationId: string,
  onDbChange: (payload: any) => void
) => {
  const channel = supabase.channel(`messages-conv-${conversationId}`);
  
  const handleDbChange = async (payload: any) => {
    console.log(
      `%c[handleDbChange] Payload reçu (${payload.eventType}):`,
      'color: green; font-weight: bold;', 
      payload
    );
    onDbChange(payload);
  };

  channel.on('postgres_changes', {
    event: '*',
    schema: 'public',
    table: 'messages',
    filter: `conversation_id=eq.${conversationId}`
  }, handleDbChange)
  .subscribe((status, err) => {
    if (err) {
      console.error(`Error subscribing to conversation ${conversationId}:`, err);
    }
  });
  
  return channel;
};

export const fetchRecentMessages = async (userId: string, limit: number = 5): Promise<any[]> => {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      *,
      sender_type,
      sender:profiles!messages_sender_id_fkey(id, display_name), 
      recipient:profiles!messages_recipient_id_fkey(id, display_name) 
    `)
    .or(`recipient_id.eq.${userId},sender_id.eq.${userId}`)
    .order('created_at', { ascending: false })
    .limit(limit);
    
  if (error) {
    console.error("Error fetching recent messages:", error);
    throw error;
  }
  
  return data || [];
};

export const _fetchRawMessages = async (userId: string, partnerId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      *,
      sender_type, 
      sender:profiles!messages_sender_id_fkey(id, display_name),
      recipient:profiles!messages_recipient_id_fkey(id, display_name)
    `)
    .or(`(sender_id.eq.${userId},recipient_id.eq.${partnerId}),(sender_id.eq.${partnerId},recipient_id.eq.${userId})`)
    .order('created_at', { ascending: true });
    
  if (error) throw error;
  
  return data || [];
};

export const deleteMessage = async (messageId: string): Promise<{ success: boolean, error?: any }> => {
  console.log(`Attempting to delete message with ID: ${messageId}`);
  
  const { error } = await supabase
    .from('messages')
    .delete()
    .eq('id', messageId);
    
  if (error) {
    console.error(`Error deleting message ${messageId}:`, error);
    return { success: false, error };
  }
  
  console.log(`Message ${messageId} deleted successfully (or RLS prevented it).`);
  return { success: true };
};
